import { useCallback } from "react";

import { useCachedResource } from "src-next/shared/cache/useCachedResource";

import { DEFAULT_WHATSAPP_BUSINESS_ACCOUNT_ID } from "../constants";
import { messageTemplatesCacheKey } from "../services/messageTemplatesCache";
import { messageTemplatesService } from "../services/messageTemplatesService";
import type { CreateMessageTemplateInput, MessageTemplate } from "../types";

const EMPTY_TEMPLATES: MessageTemplate[] = [];

interface UseMessageTemplatesResult {
  templates: MessageTemplate[];
  isLoading: boolean;
  createTemplate: (params: {
    input: CreateMessageTemplateInput;
  }) => Promise<void>;
  updateTemplate: (params: {
    input: MessageTemplate & { headerMediaFile?: File | null };
  }) => Promise<void>;
  deleteTemplate: (params: { templateId: string }) => Promise<void>;
  refreshTemplates: () => Promise<void>;
}

interface UseMessageTemplatesParams {
  whatsappBusinessAccountId?: string;
  phoneNumberId?: string | null;
  organizationId?: string;
  /**
   * Controla quando o hook pode de fato buscar templates.
   *
   * Motivação:
   * - Evitar "double-fetch" (buscar com DEFAULT e depois com WABA real) enquanto
   *   ainda não sabemos se existe endpoint/contrato ativo.
   * - Quando `enabled` é false, o hook NÃO dispara listagem automática.
   * - A lista fica no cache da sessão (`crm:message-templates:{wabaId}`).
   *   Outro caller com o mesmo WABA reutiliza a memória; fechar e abrir não busca de novo.
   *
   * Observação:
   * - Mantemos `true` como padrão para não quebrar fluxos existentes.
   */
  enabled?: boolean;
}

export function useMessageTemplates(
  params: UseMessageTemplatesParams = {},
): UseMessageTemplatesResult {
  const enabled = params.enabled ?? true;

  const explicitWabaId = (params.whatsappBusinessAccountId ?? "").trim();
  /**
   * Sem WABA explícito o service já usa a conta default (canal Fluxmed).
   * A chave usa esse mesmo id para o board, o dialog e o acompanhamento
   * não baixarem a Meta duas vezes. `enabled` continua falso até o
   * endpoint real estar resolvido, para não gravar o default cedo demais.
   */
  const resolvedWhatsappBusinessAccountId =
    explicitWabaId || DEFAULT_WHATSAPP_BUSINESS_ACCOUNT_ID;

  const resolvedPhoneNumberId =
    String(params.phoneNumberId ?? "").trim() ||
    String(import.meta.env.VITE_FLUXZAP_DEFAULT_PHONE_NUMBER_ID ?? "").trim() ||
    "379190331933594";
  const resolvedOrganizationId = String(params.organizationId ?? "").trim() || null;

  const cacheKey = messageTemplatesCacheKey(resolvedWhatsappBusinessAccountId);

  const { data, isLoading, refresh } = useCachedResource<MessageTemplate[]>({
    cacheKey,
    enabled,
    fetcher: () =>
      messageTemplatesService.list({
        whatsappBusinessAccountId: resolvedWhatsappBusinessAccountId,
      }),
  });

  const templates = data ?? EMPTY_TEMPLATES;

  const refreshTemplates = useCallback(async () => {
    await refresh();
  }, [refresh]);

  const createTemplate = useCallback(
    async ({ input }: { input: CreateMessageTemplateInput }) => {
      if (!enabled) {
        throw new Error(
          "Templates ainda não estão habilitados para carregamento. Aguarde a configuração inicial.",
        );
      }
      await messageTemplatesService.create({
        whatsappBusinessAccountId: resolvedWhatsappBusinessAccountId,
        phoneNumberId: resolvedPhoneNumberId,
        organizationId: resolvedOrganizationId,
        input,
      });
      await refresh();
    },
    [enabled, refresh, resolvedOrganizationId, resolvedPhoneNumberId, resolvedWhatsappBusinessAccountId],
  );

  const updateTemplate = useCallback(
    async ({
      input,
    }: {
      input: MessageTemplate & { headerMediaFile?: File | null };
    }) => {
      if (!enabled) {
        throw new Error(
          "Templates ainda não estão habilitados para carregamento. Aguarde a configuração inicial.",
        );
      }
      await messageTemplatesService.update({
        whatsappBusinessAccountId: resolvedWhatsappBusinessAccountId,
        phoneNumberId: resolvedPhoneNumberId,
        organizationId: resolvedOrganizationId,
        input,
      });
      await refresh();
    },
    [enabled, refresh, resolvedOrganizationId, resolvedPhoneNumberId, resolvedWhatsappBusinessAccountId],
  );

  const deleteTemplate = useCallback(
    async ({ templateId }: { templateId: string }) => {
      if (!enabled) {
        throw new Error(
          "Templates ainda não estão habilitados para carregamento. Aguarde a configuração inicial.",
        );
      }
      const template = templates.find((item) => item.id === templateId);
      if (!template) return;

      await messageTemplatesService.remove({
        whatsappBusinessAccountId: resolvedWhatsappBusinessAccountId,
        templateName: template.name,
      });
      await refresh();
    },
    [enabled, refresh, resolvedWhatsappBusinessAccountId, templates],
  );

  return {
    templates,
    isLoading,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    refreshTemplates,
  };
}
